import { ExternalLink, Phone, Play, Search } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
	Bar,
	BarChart,
	CartesianGrid,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { fetchJson, sendJson } from "../api/rest";
import {
	btn,
	Empty,
	ErrorBanner,
	fmtNumber,
	fmtRelative,
	input,
	Panel,
	Spinner,
} from "../components/flows/ui";
import { PageHeader, SectionHeading } from "../components/layout/PageHeader";
import {
	compact,
	NETWORK_LABEL,
	nextActionLabel,
	OUTCOME_COLOR,
	OUTCOME_LABEL,
	PLATFORM_LABEL,
	RESEARCH_LABEL,
	StatusDot,
} from "../components/prospects/labels";
import type {
	CallOutcome,
	Dashboard,
	ProspectList,
	ProspectRow,
	ProspectRun,
	ProspectStatus,
} from "../types/prospects";

type View = "today" | "open" | "due" | "all" | ProspectStatus;

const VIEWS: { key: View; label: string }[] = [
	{ key: "today", label: "Call today" },
	{ key: "due", label: "Follow-ups" },
	{ key: "interested", label: "Interested" },
	{ key: "not_interested", label: "Not interested" },
	{ key: "signed_up", label: "Signed up" },
	{ key: "open", label: "All open" },
	{ key: "parked", label: "Parked" },
	{ key: "all", label: "All" },
];

const SORTS = [
	{ key: "priority", label: "Priority" },
	{ key: "score", label: "Score" },
	{ key: "impressions", label: "Impressions" },
	{ key: "unlock", label: "First/second shop" },
	{ key: "shared", label: "Shared products" },
	{ key: "next", label: "Next action" },
	{ key: "name", label: "Name" },
];

const FUNNEL: ProspectStatus[] = [
	"new",
	"to_call",
	"callback",
	"interested",
	"signed_up",
];

const POLL_MS = 5000;
const field = input.replace("w-full ", "");
const errorText = (err: unknown) =>
	err instanceof Error ? err.message : String(err);

const Tile = ({
	label,
	value,
	hint,
	accent,
	to,
}: {
	label: string;
	value: string;
	hint?: string;
	accent?: boolean;
	to?: () => void;
}) => (
	<button
		type="button"
		onClick={to}
		disabled={!to}
		className={`card bg-white p-4 text-left transition-colors ${to ? "hover:border-zinc-300" : "cursor-default"}`}
	>
		<div className="text-[11px] font-medium uppercase tracking-wide text-zinc-500">
			{label}
		</div>
		<div
			className={`mt-2 text-[26px] font-semibold leading-none tabular-nums ${accent ? "text-ink" : "text-zinc-700"}`}
		>
			{value}
			{accent && (
				<span className="ml-2 inline-block h-2 w-2 -translate-y-1 rounded-full bg-brand" />
			)}
		</div>
		{hint && <div className="mt-1.5 text-[11px] text-zinc-400">{hint}</div>}
	</button>
);

const whyLine = (p: ProspectRow) => {
	if (p.researchStatus === "no_domain") return "Website needed before research";
	if (p.researchStatus === "unreachable") return "Site did not answer";
	if (p.researchStatus === "pending") return "Not researched yet";
	const parts: string[] = [];
	if (p.sharedEstimate) parts.push(`~${compact(p.sharedEstimate)} shared`);
	if (p.impressions) parts.push(`${compact(p.impressions)} impressions`);
	if (p.unlockEstimate) parts.push(`${compact(p.unlockEstimate)} at 0–1 shops`);
	return parts.length ? parts.join(" · ") : "No overlap found";
};

const CallCard = ({ p, rank }: { p: ProspectRow; rank?: number }) => {
	const next = nextActionLabel(p.nextActionAt);
	return (
		<Link
			to={`/cold-calls/${p.id}`}
			className="group flex items-start gap-3 border-b border-hairline py-3 last:border-b-0"
		>
			{rank !== undefined && (
				<span className="mt-0.5 w-5 shrink-0 text-right text-[12px] tabular-nums text-zinc-400">
					{rank}
				</span>
			)}
			<div className="min-w-0 flex-1">
				<div className="flex items-baseline gap-2">
					<span className="truncate text-[13px] font-semibold text-ink group-hover:underline">
						{p.name}
					</span>
					{p.lane === "winback" && (
						<span className="rounded-full bg-amber-50 px-1.5 text-[10px] font-medium text-amber-700">
							win-back
						</span>
					)}
				</div>
				<div className="mt-0.5 truncate text-[12px] text-zinc-500">
					{next ? (
						<span className={next.late ? "text-rose-600" : "text-zinc-700"}>
							{next.text}
						</span>
					) : (
						whyLine(p)
					)}
				</div>
			</div>
			<div className="shrink-0 text-right">
				{p.phone ? (
					<span className="inline-flex items-center gap-1 text-[12px] tabular-nums text-zinc-700">
						<Phone className="h-3 w-3" />
						{p.phone.replace("+45 ", "")}
					</span>
				) : (
					<span className="text-[11px] text-zinc-400">no phone</span>
				)}
				<div className="mt-0.5 text-[11px] text-zinc-400">
					<StatusDot status={p.status} />
				</div>
			</div>
		</Link>
	);
};

const CallsChart = ({ data }: { data: Dashboard["callsByDay"] }) => {
	const rows = useMemo(() => {
		const days: Array<Record<string, number | string>> = [];
		const today = new Date();
		for (let i = 13; i >= 0; i--) {
			const d = new Date(today.getTime() - i * 86_400_000);
			days.push({
				date: d.toISOString().slice(0, 10),
				label: `${d.getDate()}/${d.getMonth() + 1}`,
			});
		}
		for (const r of data) {
			const day = days.find((d) => d.date === r.date);
			if (day) day[r.outcome] = Number(day[r.outcome] ?? 0) + r.calls;
		}
		return days;
	}, [data]);
	const outcomes = Object.keys(OUTCOME_LABEL);
	if (data.length === 0)
		return (
			<div className="flex h-[160px] items-center justify-center text-[12px] text-zinc-400">
				No calls logged in the last 14 days.
			</div>
		);
	return (
		<ResponsiveContainer width="100%" height={160}>
			<BarChart data={rows} margin={{ top: 4, right: 4, bottom: 0, left: -24 }}>
				<CartesianGrid vertical={false} stroke="#f4f4f5" />
				<XAxis dataKey="label" tick={{ fontSize: 10 }} tickLine={false} />
				<YAxis allowDecimals={false} tick={{ fontSize: 10 }} tickLine={false} />
				<Tooltip
					formatter={(v: number, name: string) => [
						v,
						OUTCOME_LABEL[name as CallOutcome] ?? name,
					]}
				/>
				{outcomes.map((o) => (
					<Bar key={o} dataKey={o} stackId="calls" fill={OUTCOME_COLOR[o]} />
				))}
			</BarChart>
		</ResponsiveContainer>
	);
};

export default function ColdCalls() {
	const [dash, setDash] = useState<Dashboard | null>(null);
	const [list, setList] = useState<ProspectList | null>(null);
	const [categories, setCategories] = useState<string[]>([]);
	const [view, setView] = useState<View>("today");
	const [q, setQ] = useState("");
	const [query, setQuery] = useState("");
	const [lane, setLane] = useState("");
	const [category, setCategory] = useState("");
	const [hasPhone, setHasPhone] = useState(false);
	const [sort, setSort] = useState("priority");
	const [page, setPage] = useState(1);
	const [error, setError] = useState<string | null>(null);
	const [notice, setNotice] = useState<string | null>(null);
	const [run, setRun] = useState<ProspectRun | null>(null);
	const [batch, setBatch] = useState(50);
	const [starting, setStarting] = useState(false);

	const loadDashboard = useCallback(async () => {
		try {
			const d = await fetchJson<Dashboard>("/prospects/dashboard");
			setDash(d);
			setRun(d.lastRun);
		} catch (err) {
			setError(errorText(err));
		}
	}, []);

	const loadList = useCallback(async () => {
		const params = new URLSearchParams({ sort, page: String(page) });
		if (["today", "open", "due"].includes(view)) params.set("view", view);
		else if (view !== "all") params.set("status", view);
		if (query) params.set("q", query);
		if (lane) params.set("lane", lane);
		if (category) params.set("category", category);
		if (hasPhone) params.set("hasPhone", "1");
		try {
			setList(await fetchJson<ProspectList>(`/prospects?${params}`));
		} catch (err) {
			setError(errorText(err));
		}
	}, [view, query, lane, category, hasPhone, sort, page]);

	useEffect(() => {
		loadDashboard();
		fetchJson<string[]>("/prospects/categories")
			.then(setCategories)
			.catch(() => {});
	}, [loadDashboard]);

	useEffect(() => {
		loadList();
	}, [loadList]);

	useEffect(() => {
		if (run?.status !== "running") return;
		const timer = setInterval(async () => {
			try {
				const latest = await fetchJson<ProspectRun>(
					`/prospects/runs/${run.id}`,
				);
				setRun(latest);
				if (latest.status !== "running") {
					loadDashboard();
					loadList();
				}
			} catch {
				/* the next tick tries again */
			}
		}, POLL_MS);
		return () => clearInterval(timer);
	}, [run, loadDashboard, loadList]);

	const startResearch = async () => {
		setStarting(true);
		setError(null);
		try {
			const res = await sendJson<
				{ success: true; runId: number } | { success: false; error: string }
			>("/prospects/runs", "POST", { limit: batch });
			if (!res.success) throw new Error(res.error);
			setNotice(`Researching the next ${batch} shops (run #${res.runId}).`);
			setRun(await fetchJson<ProspectRun>(`/prospects/runs/${res.runId}`));
		} catch (err) {
			setError(errorText(err));
		} finally {
			setStarting(false);
		}
	};

	const pick = (next: View) => {
		setView(next);
		setPage(1);
		document
			.getElementById("prospect-list")
			?.scrollIntoView({ behavior: "smooth" });
	};

	if (!dash && !error) return <Spinner />;
	const t = dash?.totals;
	const funnelMax = Math.max(
		1,
		...FUNNEL.map((s) => dash?.statusCounts[s] ?? 0),
	);
	const decided =
		(t?.interested ?? 0) + (t?.notInterested ?? 0) + (t?.signedUp ?? 0);

	return (
		<div className="space-y-8">
			<PageHeader
				kicker="Sales · Cold calls"
				title="Cold calls."
				qualifier="Shops worth a phone call."
				description={
					<p>
						Shops from PriceRunner&apos;s list that we do not have yet, ranked
						by the Google demand on products they sell and we list. Products
						where they would be the first or second shop count three times,
						because that is where a new shop makes a page comparable.
					</p>
				}
				actions={
					<>
						<select
							className={field}
							value={batch}
							onChange={(e) => setBatch(Number(e.target.value))}
						>
							{[10, 50, 100, 250].map((n) => (
								<option key={n} value={n}>
									Next {n}
								</option>
							))}
						</select>
						<button
							type="button"
							className={btn.primary}
							disabled={starting || run?.status === "running"}
							onClick={startResearch}
						>
							<Play className="h-3.5 w-3.5" />
							Research shops
						</button>
						{run && (
							<span className="text-[11px] text-zinc-500">
								<StatusDot
									status={run.status}
									label={`Run #${run.id} ${run.status}`}
								/>
								{run.summary ? ` · ${run.summary.replace(/^🎉\s*/, "")}` : ""}
							</span>
						)}
					</>
				}
			/>
			<ErrorBanner error={error} />
			{notice && (
				<div className="rounded-xl border border-hairline bg-white px-4 py-3 text-[13px] text-zinc-600">
					{notice}
				</div>
			)}

			{t && (
				<div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6">
					<Tile
						label="Due today"
						value={fmtNumber(t.dueToday)}
						hint={
							t.overdue ? `${t.overdue} overdue` : "follow-ups and callbacks"
						}
						accent={t.dueToday > 0}
						to={() => pick("due")}
					/>
					<Tile
						label="Ready to call"
						value={fmtNumber(t.callable)}
						hint={`${fmtNumber(t.researched)} researched of ${fmtNumber(t.prospects)}`}
						to={() => pick("today")}
					/>
					<Tile
						label="Calls this week"
						value={fmtNumber(t.callsThisWeek)}
						hint={`${t.callsToday} today`}
					/>
					<Tile
						label="Interested"
						value={fmtNumber(t.interested)}
						hint={
							decided
								? `${Math.round(((t.interested + t.signedUp) / decided) * 100)} % of decided calls`
								: "the yes pile"
						}
						to={() => pick("interested")}
					/>
					<Tile
						label="Not interested"
						value={fmtNumber(t.notInterested)}
						hint="the no pile"
						to={() => pick("not_interested")}
					/>
					<Tile
						label="Signed up"
						value={fmtNumber(t.signedUp)}
						to={() => pick("signed_up")}
					/>
				</div>
			)}

			{dash && (
				<div className="grid gap-6 xl:grid-cols-12">
					<div className="space-y-6 xl:col-span-5">
						<Panel
							title="Call today"
							hint="Follow-ups due first, then the best uncalled shops"
						>
							{dash.due.length === 0 && dash.nextUp.length === 0 ? (
								<Empty>
									Nothing to call yet. Research shops to fill the list.
								</Empty>
							) : (
								<div>
									{dash.due.map((p) => (
										<CallCard key={`d${p.id}`} p={p} />
									))}
									{dash.nextUp.map((p, i) => (
										<CallCard key={`n${p.id}`} p={p} rank={i + 1} />
									))}
								</div>
							)}
						</Panel>
					</div>
					<div className="space-y-6 xl:col-span-7">
						<div className="grid gap-6 md:grid-cols-2">
							<Panel title="Pipeline">
								<div className="space-y-2.5">
									{FUNNEL.map((s) => {
										const n = dash.statusCounts[s] ?? 0;
										return (
											<button
												type="button"
												key={s}
												onClick={() => pick(s)}
												className="block w-full text-left"
											>
												<div className="flex justify-between text-[12px]">
													<StatusDot status={s} />
													<span className="tabular-nums text-zinc-600">
														{fmtNumber(n)}
													</span>
												</div>
												<div className="mt-1 h-1.5 rounded-full bg-zinc-100">
													<div
														className="h-1.5 rounded-full bg-zinc-800"
														style={{
															width: `${Math.max(n ? 2 : 0, (n / funnelMax) * 100)}%`,
														}}
													/>
												</div>
											</button>
										);
									})}
									<div className="flex flex-wrap gap-x-4 gap-y-1 pt-2 text-[11px] text-zinc-500">
										<span>
											Not interested{" "}
											{fmtNumber(dash.statusCounts.not_interested)}
										</span>
										<span>Parked {fmtNumber(dash.statusCounts.parked)}</span>
										<span>
											Do not contact{" "}
											{fmtNumber(dash.statusCounts.do_not_contact)}
										</span>
										<span>
											Win-back lane {fmtNumber(dash.laneCounts.winback ?? 0)}
										</span>
										<span>Website needed {fmtNumber(t?.noDomain ?? 0)}</span>
									</div>
								</div>
							</Panel>
							<Panel title="Calls" hint="Last 14 days, by outcome">
								<CallsChart data={dash.callsByDay} />
								<div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-zinc-500">
									{Object.entries(dash.outcomesThisMonth).map(([o, n]) => (
										<span key={o} className="inline-flex items-center gap-1">
											<span
												className="h-2 w-2 rounded-full"
												style={{ background: OUTCOME_COLOR[o] ?? "#d4d4d8" }}
											/>
											{OUTCOME_LABEL[o as CallOutcome] ?? o} {n}
										</span>
									))}
								</div>
							</Panel>
						</div>
						<Panel
							title="Where to focus"
							hint="Open, researched shops by their main category"
						>
							{dash.categories.length === 0 ? (
								<Empty>No researched shops with matched products yet.</Empty>
							) : (
								<table className="w-full text-[13px]">
									<thead>
										<tr className="border-b border-hairline text-left text-[11px] uppercase tracking-wide text-zinc-500">
											<th className="py-2 font-medium">Category</th>
											<th className="py-2 text-right font-medium">Shops</th>
											<th className="py-2 text-right font-medium">
												Impressions
											</th>
											<th className="py-2 text-right font-medium">Score</th>
										</tr>
									</thead>
									<tbody>
										{dash.categories.map((c) => (
											<tr
												key={c.name}
												className="cursor-pointer border-b border-hairline last:border-b-0 hover:bg-canvas"
												onClick={() => {
													setCategory(c.name);
													pick("open");
												}}
											>
												<td className="py-2">{c.name}</td>
												<td className="py-2 text-right tabular-nums">
													{c.prospects}
												</td>
												<td className="py-2 text-right tabular-nums">
													{compact(c.impressions)}
												</td>
												<td className="py-2 text-right tabular-nums">
													{compact(Math.round(c.score))}
												</td>
											</tr>
										))}
									</tbody>
								</table>
							)}
						</Panel>
						<Panel title="Recent activity">
							{dash.recent.length === 0 ? (
								<Empty>Calls and notes show up here.</Empty>
							) : (
								<ul className="space-y-2.5">
									{dash.recent.map((a) => (
										<li key={a.id} className="flex gap-3 text-[13px]">
											<span className="w-24 shrink-0 text-[11px] text-zinc-400">
												{fmtRelative(a.createdAt)}
											</span>
											<div className="min-w-0">
												<Link
													to={`/cold-calls/${a.prospectId}`}
													className="font-medium text-ink hover:underline"
												>
													{a.prospectName}
												</Link>{" "}
												<span className="text-zinc-500">
													{a.kind === "call" && a.outcome
														? `· ${OUTCOME_LABEL[a.outcome as CallOutcome] ?? a.outcome}`
														: a.kind === "status"
															? `· ${a.body}`
															: "· note"}
												</span>
												{a.body && a.kind !== "status" && (
													<div className="truncate text-[12px] text-zinc-500">
														{a.body}
													</div>
												)}
											</div>
										</li>
									))}
								</ul>
							)}
						</Panel>
					</div>
				</div>
			)}

			<section id="prospect-list" className="space-y-4">
				<SectionHeading
					kicker="All prospects"
					title="The list."
					qualifier={list ? `${fmtNumber(list.total)} shops` : undefined}
				/>
				<div className="flex flex-wrap items-center justify-between gap-3">
					<div className="flex flex-wrap gap-1">
						{VIEWS.map((v) => (
							<button
								key={v.key}
								type="button"
								onClick={() => {
									setView(v.key);
									setPage(1);
								}}
								className={`rounded px-3 py-1.5 text-[13px] ${view === v.key ? "bg-primary text-white" : "text-nav hover:bg-canvas"}`}
							>
								{v.label}
							</button>
						))}
					</div>
					<form
						className="flex flex-wrap items-center gap-2"
						onSubmit={(e) => {
							e.preventDefault();
							setPage(1);
							setQuery(q.trim());
						}}
					>
						<div className="relative">
							<Search className="pointer-events-none absolute top-2.5 left-2.5 h-3.5 w-3.5 text-zinc-400" />
							<input
								className={`${field} w-52 pl-8`}
								placeholder="Shop, domain or contact"
								value={q}
								onChange={(e) => setQ(e.target.value)}
							/>
						</div>
						<select
							className={field}
							value={lane}
							onChange={(e) => {
								setLane(e.target.value);
								setPage(1);
							}}
						>
							<option value="">All lanes</option>
							<option value="new">New shops</option>
							<option value="winback">Win-back</option>
						</select>
						<select
							className={`${field} max-w-48`}
							value={category}
							onChange={(e) => {
								setCategory(e.target.value);
								setPage(1);
							}}
						>
							<option value="">All categories</option>
							{categories.map((c) => (
								<option key={c} value={c}>
									{c}
								</option>
							))}
						</select>
						<label className="flex items-center gap-1.5 text-[12px] text-zinc-600">
							<input
								type="checkbox"
								checked={hasPhone}
								onChange={(e) => {
									setHasPhone(e.target.checked);
									setPage(1);
								}}
							/>
							Has phone
						</label>
						<select
							className={field}
							value={sort}
							onChange={(e) => {
								setSort(e.target.value);
								setPage(1);
							}}
						>
							{SORTS.map((s) => (
								<option key={s.key} value={s.key}>
									Sort: {s.label}
								</option>
							))}
						</select>
					</form>
				</div>

				<div className="card overflow-x-auto bg-white">
					<table className="w-full min-w-[1100px] text-[13px]">
						<thead>
							<tr className="border-b border-hairline text-left text-[11px] uppercase tracking-wide text-zinc-500">
								<th className="py-2.5 pr-2 pl-4 text-right font-medium">#</th>
								<th className="py-2.5 pr-4 font-medium">Shop</th>
								<th className="py-2.5 pr-3 text-right font-medium">Score</th>
								<th
									className="py-2.5 pr-3 text-right font-medium"
									title="Their products we list, estimated"
								>
									Shared
								</th>
								<th
									className="py-2.5 pr-3 text-right font-medium"
									title="Google impressions on the shared products, last 28 days"
								>
									Impr. 28d
								</th>
								<th
									className="py-2.5 pr-3 text-right font-medium"
									title="Shared products where they would be the first or second shop"
								>
									0–1 shops
								</th>
								<th
									className="py-2.5 pr-3 text-right font-medium"
									title="Sampled products where they are cheaper than our lowest price"
								>
									Cheaper
								</th>
								<th className="py-2.5 pr-3 font-medium">Category</th>
								<th className="py-2.5 pr-3 font-medium">Phone</th>
								<th className="py-2.5 pr-3 font-medium">Status</th>
								<th className="py-2.5 pr-4 font-medium">Next / last</th>
							</tr>
						</thead>
						<tbody>
							{list?.items.map((p, i) => {
								const next = nextActionLabel(p.nextActionAt);
								return (
									<tr
										key={p.id}
										className="border-b border-hairline last:border-b-0 hover:bg-canvas"
									>
										<td className="py-2.5 pr-2 pl-4 text-right text-[12px] tabular-nums text-zinc-400">
											{(list.page - 1) * 50 + i + 1}
										</td>
										<td className="max-w-[260px] py-2.5 pr-4">
											<Link
												to={`/cold-calls/${p.id}`}
												className="font-medium text-ink hover:underline"
											>
												{p.name}
											</Link>
											{p.lane === "winback" && (
												<span className="ml-1.5 rounded-full bg-amber-50 px-1.5 text-[10px] font-medium text-amber-700">
													win-back
												</span>
											)}
											<div className="flex items-center gap-1.5 truncate text-[11px] text-zinc-400">
												{p.domain ? (
													<a
														href={`https://${p.domain}`}
														target="_blank"
														rel="noreferrer"
														className="inline-flex items-center gap-0.5 hover:text-zinc-600"
													>
														{p.domain}
														<ExternalLink className="h-2.5 w-2.5" />
													</a>
												) : (
													<span>{RESEARCH_LABEL[p.researchStatus]}</span>
												)}
												{p.platform && (
													<span>
														· {PLATFORM_LABEL[p.platform] ?? p.platform}
													</span>
												)}
												{p.network && (
													<span className="text-amber-700">
														· {NETWORK_LABEL[p.network] ?? p.network}
													</span>
												)}
												{p.isDanish === false && (
													<span className="text-rose-600">· not Danish</span>
												)}
											</div>
										</td>
										<td className="py-2.5 pr-3 text-right font-semibold tabular-nums">
											{p.score === null ? "—" : compact(Math.round(p.score))}
										</td>
										<td className="py-2.5 pr-3 text-right tabular-nums">
											{compact(p.sharedEstimate)}
										</td>
										<td className="py-2.5 pr-3 text-right tabular-nums">
											{compact(p.impressions)}
										</td>
										<td className="py-2.5 pr-3 text-right tabular-nums">
											{compact(p.unlockEstimate)}
										</td>
										<td className="py-2.5 pr-3 text-right tabular-nums text-zinc-600">
											{p.comparedCount
												? `${p.cheaperCount}/${p.comparedCount}`
												: "—"}
										</td>
										<td className="max-w-[160px] truncate py-2.5 pr-3 text-zinc-600">
											{p.topCategory ?? "—"}
										</td>
										<td className="py-2.5 pr-3 whitespace-nowrap tabular-nums">
											{p.phone ? (
												<a
													href={`tel:${p.phone.replace(/\s/g, "")}`}
													className="hover:underline"
												>
													{p.phone.replace("+45 ", "")}
												</a>
											) : (
												<span className="text-zinc-300">—</span>
											)}
										</td>
										<td className="py-2.5 pr-3 text-[12px]">
											<StatusDot status={p.status} />
										</td>
										<td className="py-2.5 pr-4 text-[12px] whitespace-nowrap text-zinc-500">
											{next ? (
												<span
													className={
														next.late ? "text-rose-600" : "text-zinc-700"
													}
												>
													{next.text}
												</span>
											) : p.lastActivityAt ? (
												`${p.calls} call${p.calls === 1 ? "" : "s"} · ${fmtRelative(p.lastActivityAt)}`
											) : (
												"—"
											)}
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
					{list && list.items.length === 0 && (
						<div className="p-6">
							<Empty>No shops match. Try another view or filter.</Empty>
						</div>
					)}
				</div>
				{list && list.totalPages > 1 && (
					<div className="flex items-center justify-end gap-2 text-[13px] text-zinc-500">
						<button
							type="button"
							className={btn.ghost}
							disabled={page <= 1}
							onClick={() => setPage(page - 1)}
						>
							Previous
						</button>
						<span>
							Page {list.page} of {list.totalPages}
						</span>
						<button
							type="button"
							className={btn.ghost}
							disabled={page >= list.totalPages}
							onClick={() => setPage(page + 1)}
						>
							Next
						</button>
					</div>
				)}
			</section>
		</div>
	);
}
